/**
 * Domain hooks for earn position routes:
 *   - POST `/earn/vaults/positions`         → {@link useEarnVaultPositionDomain}
 *   - POST `/earn/portfolio/positions`      → {@link useEarnPortfolioPositionsDomain}
 *
 * The matching acknowledge mutation is exposed directly from the reducer slice
 * (`useAcknowledgeEarnVaultMutation`) and consumed by {@link useEarnAcknowledgment}.
 */

import { skipToken } from "@reduxjs/toolkit/query";

import { useEarnPortfolioPositionsQuery, useEarnVaultPositionQuery } from "../../reducers/earnVaultApi";
import { EarnVaultPositionResponse } from "../../types";

export type UseEarnVaultPositionParams = {
    vaultAddress: string | undefined;
    wallet: string | undefined;
};

/**
 * Domain hook for a single (wallet, vault) position via POST `/earn/vaults/positions`.
 * Returns the cached {@link EarnVaultPositionResponse} with bigint amounts intact; consume
 * {@link import("../view/earn-positions").useEarnVaultPosition} for rendering.
 */
export function useEarnVaultPositionDomain(params: UseEarnVaultPositionParams): {
    data: EarnVaultPositionResponse | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const { vaultAddress, wallet } = params;
    const arg = vaultAddress && wallet ? { address: vaultAddress, wallet } : skipToken;
    const query = useEarnVaultPositionQuery(arg);
    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching
    };
}

/** Domain hook for the wallet's full earn-portfolio position list. */
export function useEarnPortfolioPositionsDomain(wallet: string | undefined): {
    data: EarnVaultPositionResponse[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const query = useEarnPortfolioPositionsQuery(wallet ? { wallet } : skipToken);
    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching
    };
}
